import { injectable } from '@servicetitan/react-ioc';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { FullStory } from '@fullstory/browser';

import { EHModule, EnterpriseHubApi, UserIdentificationInfo } from '../api/eh.api';
import { SCOPE_NAME, SERVICETITAN_SUPPORT_ROLE, SUPER_ADMIN_ROLE } from '../constants';
import { HttpClient } from '../api/http-client';
import { tryGetClientError, tryGetServerError } from '../utils/api';
import { NotificationsService } from '@servicetitan/notifications';
import { EventBus } from '@servicetitan/web-components';
import { SettingsStore } from './settings.store';

export interface EhWidgetModuleState {
    module: EHModule;
    eventBus?: EventBus;
    isLoaded: boolean;
    isOpenScheduled: boolean;
}

@injectable()
export class EHStore {
    @observable
    ehModules: EHModule[] = [];

    @observable
    widgetModules = new Map<EHModule['apiScope'], EhWidgetModuleState>();

    @observable
    ehModulesLoading = false;

    @observable
    initialized = false;

    @observable
    currentDrawerModule?: EHModule;

    @observable
    username?: string;

    @observable
    isImpersonated = false;

    @observable
    isAdmin = false;

    @observable
    networkId?: string;

    @observable
    isTermsOfUseAccepted = false;

    private readonly usernameClaimName = 'name';
    private readonly userRoleClaimName =
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
    private readonly impersonatedUserIdClaimName = 'iUId';
    private readonly userIdClaimName =
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';

    private readonly userEmailClaimName =
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';

    private readonly userNetworkIdClaimName = 'nId';
    private readonly termsOfUseRequiredClaimName = 'termsOfUseRequired';

    constructor(
        private readonly httpClient: HttpClient,
        private readonly enterpriseHubApi: EnterpriseHubApi,
        private readonly notifications: NotificationsService,
        private readonly settingsStore: SettingsStore
    ) {
        makeObservable(this);
    }

    logout = () => {
        window.location.href = this.enterpriseHubApi.logoutUrl;
    };

    @action
    init = async () => {
        this.httpClient.addRequestInterceptor(req => {
            const headers = req.headers ?? {};
            headers['Eh-Requested-By'] = SCOPE_NAME;
            req.headers = headers;
            return req;
        });
        this.httpClient.addResponseRejectedInterceptor(error => {
            const serverError = tryGetServerError(error);
            if (serverError) {
                this.notifications.error({
                    title: serverError.getTitle(),
                    message: serverError.getMessage(),
                    duration: 0,
                });
            }
            throw error;
        });
        try {
            this.ehModulesLoading = true;
            const [{ data: userData }, { data: modules }, { data: configs }] = await Promise.all([
                this.enterpriseHubApi.getLoggedInUserInfo(),
                this.enterpriseHubApi.getApps(),
                this.enterpriseHubApi.getConfigs(),
            ]);

            this.settingsStore.initializeSettings(configs);

            runInAction(() => {
                const claims = userData.reduce((acc, curr) => {
                    acc[curr.key] = curr.value;
                    return acc;
                }, {} as Record<string, string>);

                const username = claims[this.usernameClaimName];
                const userId = claims[this.userIdClaimName];
                const userEmail = claims[this.userEmailClaimName];
                const userRole = claims[this.userRoleClaimName];
                const userNetworkId = claims[this.userNetworkIdClaimName];

                this.isAdmin = userRole === SUPER_ADMIN_ROLE || userRole === SERVICETITAN_SUPPORT_ROLE;
                this.isImpersonated = this.impersonatedUserIdClaimName in claims;
                this.isTermsOfUseAccepted = claims[this.termsOfUseRequiredClaimName] !== 'True';

                this.ehModules = modules;
                this.widgetModules = new Map(
                    modules
                        .filter(m => m.appType === 'widget')
                        .map(m => [
                            m.apiScope,
                            {
                                module: m,
                                isLoaded: false,
                                isOpenScheduled: false,
                            },
                        ]),
                );

                this.username = username;
                this.networkId = userNetworkId;

                this.identifyAnalytics({
                    id: userId,
                    email: userEmail,
                    name: this.username,
                    role: userRole,
                    networkId: userNetworkId,
                });
            });
        } catch (error) {
            // console.log(error);
            const clientError = tryGetClientError(error);
            if (clientError) {
                this.notifications.error({
                    title: clientError.getTitle(),
                    message: clientError.getMessage(),
                    duration: 0,
                });
            }
        } finally {
            runInAction(() => {
                this.ehModulesLoading = false;
                this.initialized = true;
            });
        }
    };

    @action
    setWidgetModuleState = (
        apiScope: EHModule['apiScope'],
        state: Partial<EhWidgetModuleState>,
    ) => {
        this.widgetModules.set(apiScope, {
            ...this.widgetModules.get(apiScope)!,
            ...state,
        });
    };

    identifyAnalytics = (user: UserIdentificationInfo) => {
        FullStory('setIdentity', {
            uid: user.id,
            properties: {
                email: user.email,
                displayName: user.name,
            },
        });

        pendo.events.ready(() => {
            pendo.identify({
                account: {
                    ...user,
                },
                visitor: {
                    ...user,
                },
            });
        });
    };

    @computed
    get routeModules() {
        return this.ehModules.filter(m => m.appType === 'route');
    }

    @computed
    get headerModules() {
        return this.ehModules.filter(m => m.linkPlacement === 'header');
    }

    @computed
    get contentModules() {
        return this.ehModules.filter(m => m.linkPlacement === 'content');
    }

    @computed
    get routeContentModules() {
        return this.contentModules.filter(m => m.appType === 'route');
    }
}
